import React, { useCallback, useState, useEffect } from "react";
import ReactDataGrid from "react-data-grid";
import { useField } from "react-final-form";
import { findIndex } from "lodash";
import { useNotify } from "react-admin";
import { isNumber } from "utils/tools";

interface IAreaDatagrid {
  edit?: boolean;
  areas: ISubcontractorAreas[];
  isDisabled?: boolean;
}

interface IRateChartInsert {
  subcontractorAreaId: number;
  ratePerStudent: number;
}

const PriceFormatter = ({ value }: any) => {
  return (
    <span>
      {Number(Math.round((Number(value) + Number.EPSILON) * 100) / 100)}
    </span>
  );
};

const columns = [
  { key: "area", name: "Area" },
  {
    key: "ratePerStudent",
    name: "Rate Per Student",
    editable: true,
    formatter: PriceFormatter,
  },
];

const CapacityIncentiveRateChartAreaDatagrid = ({
  areas,
  edit,
  isDisabled,
}: IAreaDatagrid) => {
  const [capacityIncentiveRates, setCapacityIncentiveRates] = useState<
    ICapacityIncentive[] | []
  >([]);
  const [isLoaded, setLoaded] = useState(false);

  const notify = useNotify();

  const capacityIncentiveRateChartItems = useField("capacityIncentiveRates");
  const capacityIncentiveRatesToSend = useField("capacityIncentiveRatesToSend");

  //populate capacityIncentiveRates data with updated areas data from parent component
  useEffect(() => {
    let rowData: ICapacityIncentive[] = [];
    if (!edit) {
      //prefill empty placeholder for create-case
      areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
        const data: ICapacityIncentive = {
          area: `Area ${order} (${maxDistanceInMeters}m)`,
          subcontractorAreaId: id,
          ratePerStudent: 0,
        };
        rowData.push(data);
      });
    }
    setCapacityIncentiveRates(rowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  useEffect(() => {
    if (areas.length !== 0) {
      if (!isLoaded && capacityIncentiveRateChartItems.input.value !== "") {
        let rowData: ICapacityIncentive[] = [];

        areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
          const data: ICapacityIncentive = {
            area: `Area ${order} (${maxDistanceInMeters}m)`,
            subcontractorAreaId: id,
            ratePerStudent: 0,
          };
          rowData.push(data);
        });

        capacityIncentiveRateChartItems.input.value.forEach(
          (capacityIncentiveRate: any) => {
            let areaIndex = findIndex(rowData, {
              subcontractorAreaId: capacityIncentiveRate.subcontractorAreaId,
            });

            if (areaIndex !== -1) {
              rowData[areaIndex].ratePerStudent =
                capacityIncentiveRate.ratePerStudent / 100;
            }
          },
        );

        setCapacityIncentiveRates(rowData);
        setLoaded(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityIncentiveRateChartItems.input.value, areas]);

  //update react-form data provider if there is a change in capacityIncentiveRates
  useEffect(() => {
    const insertData: IRateChartInsert[] = [];

    capacityIncentiveRates.forEach(
      (capacityIncentiveRate: ICapacityIncentive) => {
        insertData.push({
          subcontractorAreaId: capacityIncentiveRate.subcontractorAreaId,
          ratePerStudent: Number(capacityIncentiveRate.ratePerStudent) * 100,
        });
      },
    );

    capacityIncentiveRatesToSend.input.onChange(insertData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityIncentiveRates]);

  const onGridRowsUpdated = useCallback(
    ({
      fromRow,
      toRow,
      updated,
    }: ReactDataGrid.GridRowsUpdatedEvent<ICapacityIncentive>) => {
      const updatedRows = capacityIncentiveRates.slice();

      if (
        updated.hasOwnProperty("ratePerStudent") &&
        !isNumber(Number(updated.ratePerStudent))
      ) {
        return notify(`Please input Number`, "warning");
      }

      for (let i = fromRow; i <= toRow; i++) {
        updatedRows[i] = { ...updatedRows[i], ...updated };
      }

      setCapacityIncentiveRates(updatedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setCapacityIncentiveRates, capacityIncentiveRates],
  );

  if (areas.length === 0) {
    return <h4>Subcontractor Area data is not set.</h4>;
  }

  if (capacityIncentiveRates.length === 0) {
    return <h4>Loading...</h4>;
  } else {
    return (
      <ReactDataGrid
        columns={columns}
        rowGetter={(i) => capacityIncentiveRates[i]}
        rowsCount={capacityIncentiveRates.length}
        enableCellSelect={isDisabled ? false : true}
        onGridRowsUpdated={onGridRowsUpdated}
        minWidth={700}
      />
    );
  }
};
export default CapacityIncentiveRateChartAreaDatagrid;
